import React, { useCallback, useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import 'trix';
import 'trix/dist/trix.css';
import { AuthContext } from '../contexts/auth';
import ListBoxQueryMultiSelectEmployeesActiveContract from './list_box_query_employee_active_contract';
import Swal from 'sweetalert2';

const FormCreaRimborsoCompany = ({ submitForm, afterSubmit }) => {
  const { token } = useContext(AuthContext);

  const [file, setFile] = useState(null);
  const [formValues, setFormValues] = useState({
    selectedEmployee: null,
    title: '',
    amount: '',
    date: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const descriptionRef = useRef();

  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile?.size > 2 * 1024 * 1024) {
      Swal.fire('Errore', 'File troppo grande. Massimo 2MB', 'warning');
      return;
    }
    if (selectedFile) {
      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleChangeEmployee = (employee) => {
    setFormValues((prev) => ({ ...prev, selectedEmployee: employee }));
    setFormErrors((prev) => ({ ...prev, selectedEmployee: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formValues.selectedEmployee) errors.selectedEmployee = 'Dipendente richiesto';
    if (!formValues.title) errors.title = 'Oggetto richiesto';
    if (!formValues.amount || isNaN(formValues.amount) || parseFloat(formValues.amount) <= 0) {
      errors.amount = 'Importo valido richiesto';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDeleteFile = () => setFile(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append('employee_id', formValues.selectedEmployee.id);
    formData.append('title', formValues.title);
    formData.append('amount', formValues.amount);
    formData.append('date', formValues.date);
    formData.append('description', descriptionRef.current.value);
    if (file) formData.append('file_binary', file);

    setLoading(true);
    try {
      const result = await submitForm(formData);
      if (result && result.id) {
        await Swal.fire({ icon: 'success', title: 'Rimborso creato!', showConfirmButton: false, timer: 1500 });
        setFormValues({ selectedEmployee: null, title: '', amount: '', date: '', description: '' });
        setFile(null);
        afterSubmit?.();
      }
    } catch (err) {
      Swal.fire('Errore', 'Errore durante l\'invio.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleFileAccept = (e) => e.preventDefault();
    document.addEventListener("trix-file-accept", handleFileAccept);
    return () => document.removeEventListener("trix-file-accept", handleFileAccept);
  }, []);

  const handleEmployeesChange = useCallback((list) => {
    const employee = list?.[0] || null;
    setFormValues((prev) => ({ ...prev, selectedEmployee: employee }));
    setFormErrors((prev) => ({ ...prev, selectedEmployee: '' }));
  }, []);

  const selectedEmployeesMemo = useMemo(
    () => (formValues.selectedEmployee ? [formValues.selectedEmployee] : []),
    [formValues.selectedEmployee?.id]
  );

  const employeeRolePairsMemo = useMemo(
    () => (formValues.selectedEmployee ? [{ employeeId: formValues.selectedEmployee.id }] : []),
    [formValues.selectedEmployee?.id]
  );

  const css = `
    .trix-button--icon-attach, .trix-button-group--file-tools { display: none; }
  `;

  return (
    <div>
      <style>{css}</style>
      <form onSubmit={handleSubmit} className="px-4 pt-6 mb-4 bg-white">

        {/* Dipendente */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Dipendente</label>
          <ListBoxQueryMultiSelectEmployeesActiveContract
            token={token}
            selectedEmployees={selectedEmployeesMemo}
            employeeRolePairs={employeeRolePairsMemo}
            onChange={handleEmployeesChange}
            placeholder="Seleziona dipendente"
            filterBy="hr_active"
            wrapperClassName="border border-gray-300 rounded-md"
            buttonClassName="border-none shadow-none"
          />
          {formErrors.selectedEmployee && <p className="text-red-500 text-xs mt-1">{formErrors.selectedEmployee}</p>}
        </div>

        {/* Grid campi */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Oggetto</label>
            <input name="title" value={formValues.title} onChange={handleFieldChange} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm" />
            {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Data</label>
            <input type="date" name="date" value={formValues.date} onChange={handleFieldChange} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Importo</label>
            <input type="number" step="0.01" name="amount" value={formValues.amount} onChange={handleFieldChange} className="mt-1 p-2 w-full border border-gray-300 rounded-md shadow-sm" />
            {formErrors.amount && <p className="text-red-500 text-xs mt-1">{formErrors.amount}</p>}
          </div>
        </div>

        {/* Allegato */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Allegato</label>
          {file ? (
            <div className="mb-2">
              <p className="text-sm text-gray-700">File selezionato:</p>
              <ul className="text-sm text-gray-600 list-disc pl-5">
                <li><strong>Nome:</strong> {file.name}</li>
                <li><strong>Tipo:</strong> {file.type}</li>
                <li><strong>Dimensione:</strong> {(file.size / 1024).toFixed(2)} KB</li>
              </ul>
              <button type="button" onClick={handleDeleteFile} className="mt-1 text-red-500 text-sm font-semibold underline">Rimuovi</button>
            </div>
          ) : (
            <div {...getRootProps({ className: 'border-dashed border-2 border-gray-300 p-4 rounded-md cursor-pointer text-center text-sm text-gray-500' })}>
              <input {...getInputProps()} />
              <p>Trascina qui il file o clicca per selezionarlo (max 2MB)</p>
            </div>
          )}
        </div>

        {/* Descrizione */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Note aggiuntive</label>
          <input type="hidden" id="description" name="description" ref={descriptionRef} />
          <trix-editor input="description" />
        </div>

        {/* Pulsanti */}
        <div className="mt-6 flex justify-center">
          {loading ? (
            <div className="text-sm font-semibold text-gray-900">Salvataggio in corso...</div>
          ) : (
            <button type="submit" className="bg-indigo-500 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-400 w-24 font-semibold">Invia</button>
          )}
        </div>
      </form>
    </div>
  );
};

export default FormCreaRimborsoCompany;